import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MailIcon, CheckCircleIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSEO } from '../hooks/useSEO';
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  },
  transition: {
    duration: 0.6
  }
};
export function Kontakt() {
  useSEO({
    title: 'Kontakt – Tuesday Liveband aus Kempten im Allgäu',
    description: 'Kontakt zur Liveband Tuesday aus Kempten im Allgäu. Booking-Anfragen, Fragen zu Setlists, Technik und Auftritten, schreibt uns direkt per E-Mail oder Formular.',
    path: '/kontakt'
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: ''
      });
    }
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Bitte gib deinen Namen an';
    if (!formData.email.trim()) newErrors.email = 'Bitte gib deine E-Mail-Adresse an';else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Bitte gib eine gültige E-Mail-Adresse an';
    if (!formData.message.trim()) newErrors.message = 'Bitte schreibe eine Nachricht';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          access_key: '1378dc51-eb8c-468c-989e-3f1e1a63d1c9',
          subject: 'Neue Kontaktanfrage von der Tuesday Website',
          from_name: 'Tuesday Website',
          name: formData.name,
          email: formData.email,
          message: formData.message
        })
      });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          message: ''
        });
      } else {
        setSubmitError(data.message || 'Beim Senden ist ein Fehler aufgetreten. Bitte versuche es später erneut.');
      }
    } catch {
      setSubmitError('Beim Senden ist ein Fehler aufgetreten. Bitte versuche es später erneut.');
    } finally {
      setSubmitting(false);
    }
  };
  return <div className="w-full min-h-screen py-16 px-4 sm:px-6 lg:px-8" data-id="element-179">
      <div className="max-w-5xl mx-auto" data-id="element-180">
        <motion.div {...fadeInUp} className="text-center mb-12" data-id="element-181">
          <h1 className="font-display text-5xl sm:text-6xl font-bold text-primary mb-6" data-id="element-182">
            Kontakt
          </h1>
          <p className="text-muted text-lg max-w-2xl mx-auto" data-id="element-183">
            Habt ihr Fragen oder möchtet Tuesday für euer Event buchen? Meldet
            euch gerne bei uns.
          </p>
        </motion.div>

        {/* Booking CTA */}
        <motion.div {...fadeInUp} className="bg-[var(--color-secondary-hover)] border border-secondary rounded-lg p-8 text-center mb-12" data-id="element-184">
          <h2 className="font-display text-2xl font-bold text-primary mb-4" data-id="element-185">
            Tuesday buchen
          </h2>
          <p className="text-muted mb-6" data-id="element-186">
            Für Buchungsanfragen nutzt bitte unser Buchungsformular für alle
            Details zu eurem Event.
          </p>
          <Link to="/buchen" className="inline-block px-8 py-3 bg-accent text-[var(--color-text-inv)] font-medium rounded hover:bg-[var(--color-accent-solid-hover)] transition-colors" data-id="element-187">
            Zum Buchungsformular
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-188">
          {/* Contact Form */}
          <motion.div {...fadeInUp} data-id="element-189">
            <div className="bg-surface border border-border-base rounded-lg p-8" data-id="element-190">
              <h2 className="font-display text-2xl font-bold text-primary mb-6" data-id="element-191">
                Nachricht senden
              </h2>

              {submitted ? <div className="text-center py-8" data-id="element-192">
                  <CheckCircleIcon size={48} className="text-accent mx-auto mb-4" data-id="element-193" />
                  <h3 className="font-display text-xl font-bold text-primary mb-2" data-id="element-194">
                    Nachricht gesendet!
                  </h3>
                  <p className="text-muted mb-6" data-id="element-195">
                    Wir melden uns so schnell wie möglich bei dir.
                  </p>
                  <button onClick={() => setSubmitted(false)} className="px-6 py-2 border-2 border-border-base text-primary font-medium rounded hover:border-accent hover:text-accent transition-colors" data-id="element-196">
                    Weitere Nachricht senden
                  </button>
                </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-197">
                  <div data-id="element-198">
                    <label htmlFor="name" className="block text-sm font-medium text-primary mb-2" data-id="element-199">
                      Name *
                    </label>
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} className="w-full px-4 py-2 bg-input-bg border border-border-base rounded text-primary focus:outline-none focus:border-accent transition-colors" data-id="element-200" />
                    {errors.name && <p className="mt-1 text-sm text-error" data-id="element-201">{errors.name}</p>}
                  </div>

                  <div data-id="element-202">
                    <label htmlFor="email" className="block text-sm font-medium text-primary mb-2" data-id="element-203">
                      E-Mail *
                    </label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className="w-full px-4 py-2 bg-input-bg border border-border-base rounded text-primary focus:outline-none focus:border-accent transition-colors" data-id="element-204" />
                    {errors.email && <p className="mt-1 text-sm text-error" data-id="element-205">{errors.email}</p>}
                  </div>

                  <div data-id="element-206">
                    <label htmlFor="message" className="block text-sm font-medium text-primary mb-2" data-id="element-207">
                      Nachricht *
                    </label>
                    <textarea id="message" name="message" value={formData.message} onChange={handleChange} rows={5} className="w-full px-4 py-2 bg-input-bg border border-border-base rounded text-primary focus:outline-none focus:border-accent transition-colors resize-none" data-id="element-208" />
                    {errors.message && <p className="mt-1 text-sm text-error" data-id="element-209">
                        {errors.message}
                      </p>}
                  </div>

                  <button type="submit" disabled={submitting} className="w-full px-6 py-3 bg-accent text-[var(--color-text-inv)] font-medium rounded hover:bg-[var(--color-accent-solid-hover)] transition-colors disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-210">
                    {submitting ? 'Wird gesendet…' : 'Nachricht senden'}
                  </button>
                  {submitError && <p className="mt-3 text-sm text-error text-center" data-id="element-211">
                      {submitError}
                    </p>}
                </form>}
            </div>
          </motion.div>

          {/* Contact Info */}
          <motion.div {...fadeInUp} className="space-y-6" data-id="element-212">
            <div className="bg-surface border border-border-base rounded-lg p-8" data-id="element-213">
              <h3 className="font-display text-xl font-bold text-primary mb-4" data-id="element-214">
                E-Mail
              </h3>
              <a href="mailto:booking@tuesday-band.de" className="flex items-center gap-3 text-muted hover:text-accent transition-colors" data-id="element-215">
                <MailIcon size={20} data-id="element-216" />
                <span data-id="element-217">booking@tuesday-band.de</span>
              </a>
            </div>

            <div className="bg-[var(--color-accent-hover)] border border-accent rounded-lg p-6" data-id="element-218">
              <p className="text-primary text-sm" data-id="element-219">
                Bandfotos, Audio-Samples und Referenzen werden in Kürze ergänzt.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>;
}